/**
 * Small UPS edit form body — collapsible "Small UPS" details card + Additional Information (#996).
 * Mirrors RackEditFormContent. Every field is wrapped in PermissionAwareField for RBAC.
 */

import React from 'react';
import { Platform, StyleSheet } from 'react-native';

import { parseOptionalNumber } from './smallUpsListUtils';
import {
  SMALL_UPS_NOTES_SUBSECTION_FIELDS,
  SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
} from '../../constants/requestFieldDescriptors';
import { SECTION_IDS, getSectionLabel } from '../../constants/sectionIds';
import {
  SMALL_UPS_EDIT_DETAILS_CARD_SUBTITLE,
  SMALL_UPS_EDIT_STATUS_EXISTING_LABEL,
  SMALL_UPS_EDIT_TYPE_LABEL,
  SMALL_UPS_EDIT_TYPE_OPTIONS,
} from '../../constants/smallUpsConfigurationsSubsectionCopy';
import {
  SMALL_UPS_TYPE_OPTIONS,
  SMALL_UPS_INPUT_PHASE_OPTIONS,
  SMALL_UPS_MOUNTING_OPTIONS,
  SMALL_UPS_MOUNT_TYPE_OPTIONS,
  SMALL_UPS_PLACEHOLDERS,
} from '../../constants/smallUpsRequirementsUi';
import { useFieldLabels } from '../../hooks/useFieldLabels';
import { useFormFieldVisibilityContext } from '../../hooks/useFormFieldVisibilityContext';
import { RequestFormAdditionalInformation } from '../../request/steps/subsections/AdditionalInformation';
import { spacing } from '../../theme';
import {
  resolveRepeatableInstanceAttachmentSectionId,
  selectAttachmentsForRepeatableInstance,
  selectSelectedFilesForRepeatableInstance,
} from '../../utils/repeatableInstanceAttachmentSections';
import { subsectionHeaderMetricsProps } from '../../utils/subsectionHeaderMetrics';
import { booleanToYesNo } from '../../utils/yesNoBoolean';
import {
  FormRow,
  FormColumn,
  FormInput,
  FormDropdownField,
  FormYesNoChoice,
  FormTwoOptionChoice,
  NumberInput,
  formFieldInStack,
  FormStackColumnsOnWebProvider,
  StepForm,
} from '../forms';
import { LocationTagPicker } from '../locationTags/LocationTagPicker';
import { PermissionAwareField } from '../PermissionAwareField';

import type { SavedAttachment } from '../../services/attachmentService';
import type {
  SmallUpsStatus,
  SmallUpsConfiguration,
} from '../../types/smallUps';
import type { SelectedFile } from '../forms/FileUpload';

const UPS_TYPE_OPTIONS = [...SMALL_UPS_TYPE_OPTIONS];
const PHASE_OPTIONS = [...SMALL_UPS_INPUT_PHASE_OPTIONS];
const MOUNTING_OPTIONS = [...SMALL_UPS_MOUNTING_OPTIONS];
const MOUNT_TYPE_OPTIONS = [...SMALL_UPS_MOUNT_TYPE_OPTIONS];

export interface SmallUpsEditFormAttachmentProps {
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
}

export interface SmallUpsEditFormContentProps
  extends SmallUpsEditFormAttachmentProps {
  formData: Partial<SmallUpsConfiguration>;
  updateField: <K extends keyof SmallUpsConfiguration>(
    field: K,
    value: SmallUpsConfiguration[K],
  ) => void;
  disabled?: boolean;
  requestStage?: string;
  requestId?: string;
  testID?: string;
}

function smallUpsStatusToTypeLabel(status: SmallUpsStatus | undefined): string {
  return status === 'existing'
    ? SMALL_UPS_EDIT_TYPE_OPTIONS[0]
    : SMALL_UPS_EDIT_TYPE_OPTIONS[1];
}

function smallUpsTypeLabelToStatus(label: string): SmallUpsStatus {
  return label === SMALL_UPS_EDIT_STATUS_EXISTING_LABEL ? 'existing' : 'new';
}

function SmallUpsPermissionField({
  fieldId,
  requestStage,
  requestId,
  children,
}: {
  fieldId: string;
  requestStage?: string;
  requestId?: string;
  children: React.ReactNode;
}) {
  if (!requestStage) {
    return <>{children}</>;
  }
  return (
    <PermissionAwareField
      fieldId={fieldId}
      requestStage={requestStage}
      requestId={requestId}
      containerStyle={formFieldInStack}
    >
      {children}
    </PermissionAwareField>
  );
}

const SMALL_UPS_EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export const SmallUpsEditFormContent: React.FC<
  SmallUpsEditFormContentProps
> = ({
  formData,
  updateField,
  disabled = false,
  requestStage,
  requestId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  isOffline = false,
  onCaptionChange,
  pendingCaptionEdits,
  testID = 'small-ups-edit-form',
}) => {
  const permissionStage = requestStage ?? 'DRAFT';
  const { getPowerLabel } = useFieldLabels();
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails: true,
    requestStage: permissionStage,
    requestId,
  });
  const smallUpsMetrics = subsectionHeaderMetricsProps(
    SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
    formData as Record<string, unknown>,
    fieldVisibility,
  );
  const attachmentsEnabled = true;
  const configurationId = formData.id;
  const detailsBaseSectionId = SECTION_IDS.SMALL_UPS_REQUIREMENTS;
  const additionalBaseSectionId = SECTION_IDS.SMALL_UPS_ADDITIONAL;
  const detailsSectionId = resolveRepeatableInstanceAttachmentSectionId(
    detailsBaseSectionId,
    configurationId,
  );
  const additionalSectionId = resolveRepeatableInstanceAttachmentSectionId(
    additionalBaseSectionId,
    configurationId,
  );
  const detailsAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const detailsSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const additionalAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    additionalBaseSectionId,
    configurationId,
  );
  const additionalSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    additionalBaseSectionId,
    configurationId,
  );

  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      <StepForm
        title={getSectionLabel(detailsBaseSectionId)}
        titleSubtitle={SMALL_UPS_EDIT_DETAILS_CARD_SUBTITLE}
        defaultSectionExpanded
        {...SMALL_UPS_EDITOR_SUBSECTION_PERSIST}
        {...smallUpsMetrics}
        sectionId={detailsSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={detailsAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(detailsSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={detailsSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(detailsSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
      >
        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="name"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Name"
                value={formData.name || ''}
                onChangeText={value => updateField('name', value)}
                placeholder="Enter a name for this unit (e.g., UPS A)"
                disabled={disabled}
                testID={`${testID}-name`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="status"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormTwoOptionChoice
                label={SMALL_UPS_EDIT_TYPE_LABEL}
                value={smallUpsStatusToTypeLabel(formData.status)}
                options={[
                  SMALL_UPS_EDIT_TYPE_OPTIONS[0],
                  SMALL_UPS_EDIT_TYPE_OPTIONS[1],
                ]}
                onSelect={label =>
                  updateField('status', smallUpsTypeLabelToStatus(label))
                }
                disabled={disabled}
                inline
                testID={`${testID}-type`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="locationTagId"
              requestStage={requestStage}
              requestId={requestId}
            >
              <LocationTagPicker
                requestId={requestId}
                requestStage={requestStage}
                value={formData.locationTagId ?? null}
                onChange={tagId => updateField('locationTagId', tagId)}
                disabled={disabled}
                testID={`${testID}-location-tag`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsManufacturer"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Manufacturer"
                value={formData.smallUpsManufacturer || ''}
                onChangeText={value =>
                  updateField('smallUpsManufacturer', value)
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.manufacturer}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsModel"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Model"
                value={formData.smallUpsModel || ''}
                onChangeText={value => updateField('smallUpsModel', value)}
                placeholder={SMALL_UPS_PLACEHOLDERS.model}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="powerSmallUpsDescription"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Description"
                value={formData.powerSmallUpsDescription || ''}
                onChangeText={value =>
                  updateField('powerSmallUpsDescription', value)
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.description || undefined}
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsSerialNumber"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Serial Number"
                value={formData.smallUpsSerialNumber || ''}
                onChangeText={value =>
                  updateField('smallUpsSerialNumber', value)
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.serialNumber}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="powerTypesOfUps"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label="Type of UPS"
                value={formData.powerTypesOfUps || ''}
                options={UPS_TYPE_OPTIONS}
                onSelect={value =>
                  updateField(
                    'powerTypesOfUps',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.upsType}
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsInputVoltage"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Input Voltage"
                value={formData.smallUpsInputVoltage || ''}
                onChangeText={value =>
                  updateField('smallUpsInputVoltage', value)
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.inputVoltage}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsInputPlug"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Input Plug"
                value={formData.smallUpsInputPlug || ''}
                onChangeText={value => updateField('smallUpsInputPlug', value)}
                placeholder={SMALL_UPS_PLACEHOLDERS.inputPlug}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="powerInputPowerPhase"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label="Phases"
                value={formData.powerInputPowerPhase || ''}
                options={PHASE_OPTIONS}
                onSelect={value =>
                  updateField(
                    'powerInputPowerPhase',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.inputPowerPhase}
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsCapacity"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label="UPS capacity in kVA"
                value={String(formData.smallUpsCapacity ?? '')}
                onChangeText={value =>
                  updateField('smallUpsCapacity', parseOptionalNumber(value))
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.upsCapacity}
                min={0}
                showStepper={false}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsLoadPercentage"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label="Load Percentage"
                value={String(formData.smallUpsLoadPercentage ?? '')}
                onChangeText={value =>
                  updateField(
                    'smallUpsLoadPercentage',
                    parseOptionalNumber(value),
                  )
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.loadPercentage}
                min={0}
                max={100}
                showStepper={false}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsExpectedLoadGrowth"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label={getPowerLabel('Expected Load Growth')}
                value={String(formData.smallUpsExpectedLoadGrowth ?? '')}
                onChangeText={value =>
                  updateField(
                    'smallUpsExpectedLoadGrowth',
                    parseOptionalNumber(value),
                  )
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.expectedLoadGrowth}
                min={0}
                showStepper={false}
                disabled={disabled}
                testID={`${testID}-expected-load-growth`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsLoadOnGenerator"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormYesNoChoice
                label="Will load be on generator?"
                value={booleanToYesNo(formData.smallUpsLoadOnGenerator)}
                onSelect={v =>
                  updateField('smallUpsLoadOnGenerator', v === 'Yes')
                }
                disabled={disabled}
                inline
                testID={`${testID}-load-on-generator`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="powerRuntime"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Battery Runtime"
                value={formData.powerRuntime || ''}
                onChangeText={value => updateField('powerRuntime', value)}
                placeholder={SMALL_UPS_PLACEHOLDERS.runtime}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsBatteryCabinetQty"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label="Quantity of Battery Cabinets"
                value={String(formData.smallUpsBatteryCabinetQty ?? '')}
                onChangeText={value =>
                  updateField(
                    'smallUpsBatteryCabinetQty',
                    parseOptionalNumber(value),
                  )
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.batteryCabinetQty}
                min={0}
                showStepper
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsMounting"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label="Mounting (2 or 4 post)"
                value={formData.smallUpsMounting || ''}
                options={MOUNTING_OPTIONS}
                onSelect={value =>
                  updateField(
                    'smallUpsMounting',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.mounting}
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsMountType"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label="Mount Type"
                value={formData.smallUpsMountType || ''}
                options={MOUNT_TYPE_OPTIONS}
                onSelect={value =>
                  updateField(
                    'smallUpsMountType',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.mountType}
                disabled={disabled}
                allowClear
                testID={`${testID}-mount-type`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsOutlets"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Number of Outlets (Quantity of controllable / always on)"
                value={formData.smallUpsOutlets || ''}
                onChangeText={value => updateField('smallUpsOutlets', value)}
                placeholder={SMALL_UPS_PLACEHOLDERS.outlets}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsMaintenanceBypassRequired"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormYesNoChoice
                label="Maintenance bypass required"
                value={booleanToYesNo(
                  formData.smallUpsMaintenanceBypassRequired,
                )}
                onSelect={v =>
                  updateField('smallUpsMaintenanceBypassRequired', v === 'Yes')
                }
                disabled={disabled}
                inline
                testID={`${testID}-maintenance-bypass`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsBackupGenerator"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormYesNoChoice
                label="Backup Generator (Y/N)"
                value={booleanToYesNo(formData.smallUpsBackupGenerator)}
                onSelect={v =>
                  updateField('smallUpsBackupGenerator', v === 'Yes')
                }
                disabled={disabled}
                inline
                testID={`${testID}-backup-generator`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <SmallUpsPermissionField
              fieldId="smallUpsRequiredSensors"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="What sensors are required"
                value={formData.smallUpsRequiredSensors || ''}
                onChangeText={value =>
                  updateField('smallUpsRequiredSensors', value)
                }
                placeholder={SMALL_UPS_PLACEHOLDERS.requiredSensors}
                multiline
                maxLength={10000}
                disabled={disabled}
                testID={`${testID}-required-sensors`}
                containerStyle={formFieldInStack}
              />
            </SmallUpsPermissionField>
          </FormColumn>
        </FormRow>
      </StepForm>

      <RequestFormAdditionalInformation
        shell="stepForm"
        titleSubtitle="Additional notes about this small UPS not captured above."
        sectionId={additionalSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={additionalAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(additionalSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={additionalSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(additionalSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        fieldId="powerSmallUpsAdditionalInfo"
        permissionRequestStage={permissionStage}
        inputLabel="Additional Small UPS Info"
        placeholder={SMALL_UPS_PLACEHOLDERS.notes}
        value={formData.powerSmallUpsAdditionalInfo || ''}
        onChangeText={value =>
          updateField('powerSmallUpsAdditionalInfo', value)
        }
        numberOfLines={3}
        webMultilineAutoSize
        maxLength={10000}
        inputUseEditableProp={false}
        inputDisabled={disabled}
        inputContainerStyle={formFieldInStack}
        wrapFieldInRowColumn={false}
        containerStyle={styles.sectionCard}
        subsectionFields={SMALL_UPS_NOTES_SUBSECTION_FIELDS}
        headerValues={formData as Record<string, unknown>}
        defaultSectionExpanded={false}
        {...SMALL_UPS_EDITOR_SUBSECTION_PERSIST}
      />
    </FormStackColumnsOnWebProvider>
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
